import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Save, Clock, Settings as SettingsIcon, Info, Mail, Plus, X, Star, BarChart3, MessageCircle, Send, Target, Globe } from "lucide-react";
import { Switch } from "../ui/switch";
import { extractApiError } from "../../lib/apiError";

export default function SettingsTab() {
  const [form, setForm] = useState({
    min_cutoff_hours: 4,
    cutoff_window_start: "08:00",
    cutoff_window_end: "19:00",
    default_next_day_pickup_time: "10:00",
    passenger_brackets: [],
    payment_hold_enabled: false,
    payment_hold_minutes: 5,
    water_taxi_enabled: false,
    water_taxi_price: 80,
    hotel_in_venice_enabled: true,
  });
  const [notif, setNotif] = useState({
    admin_email: "", cc_emails: [],
    google_business_url: "", google_review_enabled: true,
    google_analytics_id: "",
    google_ads_conversion_id: "",
    google_ads_conversion_label: "",
    whatsapp_number: "",
    sender_email: "",
    site_url: ""
  });
  const [ccDraft, setCcDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingNotif, setSavingNotif] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [bookingRes, notifRes] = await Promise.all([
        axios.get(`${API}/booking-settings`),
        axios.get(`${API}/admin/notification-settings`)
      ]);
      setForm({
        min_cutoff_hours: bookingRes.data.min_cutoff_hours,
        cutoff_window_start: bookingRes.data.cutoff_window_start,
        cutoff_window_end: bookingRes.data.cutoff_window_end,
        default_next_day_pickup_time: bookingRes.data.default_next_day_pickup_time,
        passenger_brackets: bookingRes.data.passenger_brackets || [],
        payment_hold_enabled: !!bookingRes.data.payment_hold_enabled,
        payment_hold_minutes: bookingRes.data.payment_hold_minutes ?? 5,
        water_taxi_enabled: !!bookingRes.data.water_taxi_enabled,
        water_taxi_price: bookingRes.data.water_taxi_price ?? 80,
        hotel_in_venice_enabled: bookingRes.data.hotel_in_venice_enabled !== false,
      });
      setNotif({
        admin_email: notifRes.data.admin_email || "",
        cc_emails: notifRes.data.cc_emails || [],
        google_business_url: notifRes.data.google_business_url || "",
        google_review_enabled: notifRes.data.google_review_enabled !== false,
        google_analytics_id: notifRes.data.google_analytics_id || "",
        google_ads_conversion_id: notifRes.data.google_ads_conversion_id || "",
        google_ads_conversion_label: notifRes.data.google_ads_conversion_label || "",
        whatsapp_number: notifRes.data.whatsapp_number || "",
        sender_email: notifRes.data.sender_email || "",
        site_url: notifRes.data.site_url || ""
      });
    } catch (_e) { toast.error("Failed to load settings"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/booking-settings`, form);
      toast.success("Booking settings saved");
      load();
    } catch (e) {
      toast.error(extractApiError(e, "Failed to save"));
    } finally { setSaving(false); }
  };

  const saveNotif = async () => {
    setSavingNotif(true);
    try {
      await axios.put(`${API}/admin/notification-settings`, {
        admin_email: notif.admin_email || null,
        cc_emails: notif.cc_emails,
        google_business_url: notif.google_business_url || null,
        google_review_enabled: notif.google_review_enabled,
        google_analytics_id: notif.google_analytics_id || null,
        google_ads_conversion_id: notif.google_ads_conversion_id || null,
        google_ads_conversion_label: notif.google_ads_conversion_label || null,
        whatsapp_number: notif.whatsapp_number || null,
        sender_email: notif.sender_email || null,
        site_url: notif.site_url || null
      });
      toast.success("Settings saved");
      load();
    } catch (e) {
      toast.error(extractApiError(e, "Failed to save"));
    } finally { setSavingNotif(false); }
  };

  const addCc = () => {
    const email = ccDraft.trim().toLowerCase();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error("Invalid email"); return; }
    if (notif.cc_emails.includes(email)) { toast.error("Already added"); return; }
    setNotif(p => ({ ...p, cc_emails: [...p.cc_emails, email] }));
    setCcDraft("");
  };

  const removeCc = (email) => setNotif(p => ({ ...p, cc_emails: p.cc_emails.filter(e => e !== email) }));

  if (loading) return <div className="flex justify-center py-20"><div className="spinner" /></div>;

  return (
    <div data-testid="settings-tab" className="space-y-8 max-w-3xl">
      {/* Notification emails */}
      <section>
        <div className="mb-4">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#C5A059]" /> Notification Emails
          </h2>
          <p className="text-white/40 text-xs mt-1">Recipients receive a BCC on customer confirmations and notifications about status changes.</p>
        </div>

        <div className="bg-[#151515] border border-white/5 rounded-sm p-6 space-y-5">
          <div>
            <label className="text-white/60 text-xs uppercase mb-2 block">Main admin email</label>
            <Input
              type="email"
              placeholder="admin@taxinearme.it"
              value={notif.admin_email}
              onChange={(e) => setNotif(p => ({ ...p, admin_email: e.target.value }))}
              className="input-dark"
              data-testid="admin-email-input"
            />
          </div>

          <div>
            <label className="text-white/60 text-xs uppercase mb-2 block">CC emails</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {notif.cc_emails.length === 0 ? (
                <span className="text-white/30 text-xs italic">No CC recipients yet.</span>
              ) : (
                notif.cc_emails.map(email => (
                  <span key={email} className="inline-flex items-center gap-1 bg-[#1F1F1F] border border-white/10 text-white/70 text-xs px-2 py-1 rounded-sm" data-testid={`cc-email-${email}`}>
                    {email}
                    <button onClick={() => removeCc(email)} className="text-white/40 hover:text-red-400" data-testid={`remove-cc-${email}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))
              )}
            </div>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="cc@example.com"
                value={ccDraft}
                onChange={(e) => setCcDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCc(); } }}
                className="input-dark"
                data-testid="cc-email-input"
              />
              <Button onClick={addCc} variant="ghost" className="text-[#C5A059]" data-testid="add-cc-btn">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
          </div>

          <Button onClick={saveNotif} disabled={savingNotif} className="btn-gold w-full" data-testid="save-notif-btn">
            <Save className="w-4 h-4 mr-2" /> {savingNotif ? "Saving..." : "Save All Settings"}
          </Button>
        </div>
      </section>

      {/* Sender (Resend "From" header) */}
      <section>
        <div className="mb-4">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Send className="w-5 h-5 text-[#C5A059]" /> Email Sender (Resend "From")
          </h2>
          <p className="text-white/40 text-xs mt-1">
            Address shown in the <code className="text-[#C5A059]">From</code> header of every transactional email (quotes, confirmations, driver assignment, password resets...). Accepts a plain email or a <em>"Display Name &lt;email@domain&gt;"</em> string.
          </p>
        </div>
        <div className="bg-[#151515] border border-white/5 rounded-sm p-6 space-y-5">
          <div>
            <label className="text-white/60 text-xs uppercase mb-2 block">Sender email / From header</label>
            <Input
              type="text"
              placeholder='e.g. "TaxiNearMe <noreply@bookings.taxinearme.it>"'
              value={notif.sender_email}
              onChange={(e) => setNotif(p => ({ ...p, sender_email: e.target.value }))}
              className="input-dark font-mono"
              data-testid="sender-email-input"
              maxLength={150}
            />
            <p className="text-white/30 text-[10px] mt-1">
              Leave empty to use the default <code className="text-[#C5A059]">onboarding@resend.dev</code> sandbox sender (limited deliverability). For production, verify your custom domain in <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="text-[#C5A059] underline">Resend &raquo; Domains</a> first; using an unverified sender will silently fail at the provider.
            </p>
          </div>
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-sm p-3 text-amber-300/80 text-xs leading-relaxed">
            <p className="font-semibold mb-1">DNS reminder</p>
            <p>To send from <code className="text-amber-200">@your-domain.com</code> you must add the SPF, DKIM and (optional) DMARC records that Resend provides when you click "Verify domain". Until DNS is verified, Resend rejects the send.</p>
          </div>
          <Button onClick={saveNotif} disabled={savingNotif} className="btn-gold w-full" data-testid="save-sender-btn">
            <Save className="w-4 h-4 mr-2" /> {savingNotif ? "Saving..." : "Save Sender Email"}
          </Button>
        </div>
      </section>

      {/* Google Business / Reviews */}
      <section>
        <div className="mb-4">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Star className="w-5 h-5 text-[#C5A059]" /> Google Business / Reviews
          </h2>
          <p className="text-white/40 text-xs mt-1">A 24h-after-pickup follow-up email invites customers to leave a review (internal form + optional Google link).</p>
        </div>
        <div className="bg-[#151515] border border-white/5 rounded-sm p-6 space-y-5">
          <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="google-review-toggle">
            <Switch
              checked={notif.google_review_enabled}
              onCheckedChange={(c) => setNotif(p => ({ ...p, google_review_enabled: c }))}
              data-testid="google-review-switch"
            />
            <div>
              <p className="text-white text-sm">Show "Leave a Google review" link</p>
              <p className="text-white/40 text-xs">When OFF, customers only see the internal review form on their dashboard.</p>
            </div>
          </label>
          <div>
            <label className="text-white/60 text-xs uppercase mb-2 block">Google Business profile URL</label>
            <Input
              type="url"
              placeholder="https://g.page/r/your-business-id/review"
              value={notif.google_business_url}
              onChange={(e) => setNotif(p => ({ ...p, google_business_url: e.target.value }))}
              className="input-dark"
              disabled={!notif.google_review_enabled}
              data-testid="google-business-url"
            />
            <p className="text-white/30 text-[10px] mt-1">Tip: from Google Maps → your business → Share → Place ID. Use the "review" share link.</p>
          </div>
          <Button onClick={saveNotif} disabled={savingNotif} className="btn-gold w-full" data-testid="save-google-review-btn">
            <Save className="w-4 h-4 mr-2" /> {savingNotif ? "Saving..." : "Save Google Review Settings"}
          </Button>
        </div>
      </section>

      {/* Google Analytics */}
      <section>
        <div className="mb-4">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#C5A059]" /> Google Analytics
          </h2>
          <p className="text-white/40 text-xs mt-1">Paste your GA4 measurement ID (e.g. G-XXXXXXXXXX). The gtag.js snippet is auto-injected on every public page once saved.</p>
        </div>
        <div className="bg-[#151515] border border-white/5 rounded-sm p-6 space-y-5">
          <div>
            <label className="text-white/60 text-xs uppercase mb-2 block">Measurement ID</label>
            <Input
              type="text"
              placeholder="G-XXXXXXXXXX"
              value={notif.google_analytics_id}
              onChange={(e) => setNotif(p => ({ ...p, google_analytics_id: e.target.value.toUpperCase() }))}
              className="input-dark font-mono"
              data-testid="ga-id-input"
            />
            <p className="text-white/30 text-[10px] mt-1">Get yours at <a href="https://analytics.google.com" target="_blank" rel="noreferrer" className="text-[#C5A059] underline">analytics.google.com</a> → Admin → Data Streams → your stream ID. Leave empty to disable tracking.</p>
          </div>
          <Button onClick={saveNotif} disabled={savingNotif} className="btn-gold w-full" data-testid="save-ga-btn">
            <Save className="w-4 h-4 mr-2" /> {savingNotif ? "Saving..." : "Save Google Analytics"}
          </Button>
          {notif.google_analytics_id && (
            <p className="text-emerald-400 text-xs">✓ GA active. Hard-refresh the public site to see the gtag.js script load.</p>
          )}
        </div>
      </section>

      {/* Google Ads conversion tracking */}
      <section>
        <div className="mb-4">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Target className="w-5 h-5 text-[#C5A059]" /> Google Ads Conversion
          </h2>
          <p className="text-white/40 text-xs mt-1">
            Fire a purchase conversion on the payment success page so Google Ads can attribute booking revenue back to your campaigns.
            Both fields are required for tracking to fire. Requires the customer to have accepted <em>Marketing</em> cookies.
          </p>
        </div>
        <div className="bg-[#151515] border border-white/5 rounded-sm p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-white/60 text-xs uppercase mb-2 block">Conversion ID</label>
              <Input
                type="text"
                placeholder="AW-1234567890"
                value={notif.google_ads_conversion_id}
                onChange={(e) => setNotif(p => ({ ...p, google_ads_conversion_id: e.target.value.toUpperCase().replace(/\s/g, "") }))}
                className="input-dark font-mono"
                data-testid="ads-conversion-id-input"
              />
              <p className="text-white/30 text-[10px] mt-1">Google Ads → Tools → Conversions → your conversion → Tag setup — the <code className="text-[#C5A059]">AW-...</code> part.</p>
            </div>
            <div>
              <label className="text-white/60 text-xs uppercase mb-2 block">Conversion Label</label>
              <Input
                type="text"
                placeholder="abcDEF123"
                value={notif.google_ads_conversion_label}
                onChange={(e) => setNotif(p => ({ ...p, google_ads_conversion_label: e.target.value.replace(/\s/g, "") }))}
                className="input-dark font-mono"
                data-testid="ads-conversion-label-input"
              />
              <p className="text-white/30 text-[10px] mt-1">The alphanumeric string that appears after the <code className="text-[#C5A059]">/</code> in <code className="text-[#C5A059]">send_to</code>.</p>
            </div>
          </div>
          <Button onClick={saveNotif} disabled={savingNotif} className="btn-gold w-full" data-testid="save-ads-btn">
            <Save className="w-4 h-4 mr-2" /> {savingNotif ? "Saving..." : "Save Google Ads"}
          </Button>
          {notif.google_ads_conversion_id && notif.google_ads_conversion_label && (
            <p className="text-emerald-400 text-xs">✓ Ads tracking configured. Complete a €0.50 test booking (Stripe test mode) and confirm the <code className="text-[#C5A059]">conversion</code> event fires in the browser DevTools → Network tab.</p>
          )}
          {(!!notif.google_ads_conversion_id) !== (!!notif.google_ads_conversion_label) && (
            <p className="text-amber-400 text-xs">⚠ Both ID and Label must be filled — otherwise nothing fires.</p>
          )}
        </div>
      </section>

      {/* WhatsApp contact number (used by floating button + email templates) */}
      <section>
        <div className="mb-4">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-[#25D366]" /> WhatsApp Contact
          </h2>
          <p className="text-white/40 text-xs mt-1">Powers the floating WhatsApp button on every public page. Include the country prefix (e.g. <code className="text-[#C5A059]">+39 393 907 8663</code>).</p>
        </div>
        <div className="bg-[#151515] border border-white/5 rounded-sm p-6 space-y-5">
          <div>
            <label className="text-white/60 text-xs uppercase mb-2 block">Public WhatsApp number</label>
            <Input
              type="tel"
              placeholder="+39 393 907 8663"
              value={notif.whatsapp_number}
              onChange={(e) => setNotif(p => ({ ...p, whatsapp_number: e.target.value }))}
              className="input-dark"
              data-testid="whatsapp-number-input"
            />
            <p className="text-white/30 text-[10px] mt-1">Spaces, dashes and parentheses are stripped automatically when building the wa.me link.</p>
          </div>
          <Button onClick={saveNotif} disabled={savingNotif} className="btn-gold w-full" data-testid="save-whatsapp-btn">
            <Save className="w-4 h-4 mr-2" /> {savingNotif ? "Saving..." : "Save WhatsApp Number"}
          </Button>
        </div>
      </section>

      {/* Booking settings */}
      <section>
        <div className="mb-4">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <SettingsIcon className="w-5 h-5 text-[#C5A059]" /> Booking Settings
          </h2>
          <p className="text-white/40 text-xs mt-1">Define the minimum lead time for customer bookings.</p>
        </div>

        <div className="bg-blue-500/5 border border-blue-500/20 rounded-sm p-4 flex items-start gap-3 text-xs mb-4">
          <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-blue-300/80 leading-relaxed">
            <p className="font-semibold mb-1">How the rule works:</p>
            <p>During the validity window (e.g. 08:00 &rarr; 19:00) customers can book with the cutoff hours below.</p>
            <p>Outside the window, the first available pickup is the next day at the default time.</p>
            <p className="mt-2 italic text-blue-200/60">Example: cutoff 4h, window 08:00-19:00, next-day 10:00 &mdash; if a customer books at 22:00, the earliest pickup is tomorrow 10:00.</p>
          </div>
        </div>

        <div className="bg-[#151515] border border-white/5 rounded-sm p-6 space-y-5">
          <div>
            <label className="text-white/60 text-xs uppercase mb-2 block flex items-center gap-2">
              <Clock className="w-4 h-4" /> Minimum cutoff hours between booking and pickup
            </label>
            <div className="flex items-center gap-3">
              <Input type="number" min="0" max="72" value={form.min_cutoff_hours}
                onChange={(e) => setForm(p => ({ ...p, min_cutoff_hours: parseInt(e.target.value) || 0 }))}
                className="input-dark w-32" data-testid="cutoff-hours" />
              <span className="text-white/50 text-sm">hours</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-white/60 text-xs uppercase mb-2 block">Cutoff window — start</label>
              <Input type="time" value={form.cutoff_window_start}
                onChange={(e) => setForm(p => ({ ...p, cutoff_window_start: e.target.value }))}
                className="input-dark" data-testid="window-start" />
            </div>
            <div>
              <label className="text-white/60 text-xs uppercase mb-2 block">Cutoff window — end</label>
              <Input type="time" value={form.cutoff_window_end}
                onChange={(e) => setForm(p => ({ ...p, cutoff_window_end: e.target.value }))}
                className="input-dark" data-testid="window-end" />
            </div>
          </div>

          <div>
            <label className="text-white/60 text-xs uppercase mb-2 block">Default next-day pickup time (outside window)</label>
            <Input type="time" value={form.default_next_day_pickup_time}
              onChange={(e) => setForm(p => ({ ...p, default_next_day_pickup_time: e.target.value }))}
              className="input-dark w-40" data-testid="next-day-time" />
          </div>

          {/* "Pay to Lock It" — payment-hold countdown for immediate-confirm bookings */}
          <div className="border-t border-white/5 pt-5" data-testid="payment-hold-section">
            <div className="flex items-center justify-between mb-3">
              <div>
                <label className="text-white/60 text-xs uppercase block">"Pay to Lock It" countdown</label>
                <p className="text-white/40 text-[10px] mt-1">
                  Show a live timer on the confirmation page for immediate-confirm bookings to encourage payment within X minutes.
                  Expiry is informational — the quote stays valid at the same price after the timer ends.
                </p>
              </div>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form.payment_hold_enabled}
                  onChange={(e) => setForm(p => ({ ...p, payment_hold_enabled: e.target.checked }))}
                  className="accent-[#C5A059] w-4 h-4"
                  data-testid="payment-hold-toggle"
                />
                <span className="text-white/80 text-sm select-none">
                  {form.payment_hold_enabled ? "Enabled" : "Disabled"}
                </span>
              </label>
            </div>
            {form.payment_hold_enabled && (
              <div className="flex items-center gap-3 mt-2">
                <label className="text-white/60 text-xs uppercase">Hold duration</label>
                <Input
                  type="number" min="1" max="60"
                  value={form.payment_hold_minutes}
                  onChange={(e) => setForm(p => ({ ...p, payment_hold_minutes: Math.max(1, Math.min(60, parseInt(e.target.value) || 5)) }))}
                  className="input-dark w-20"
                  data-testid="payment-hold-minutes"
                />
                <span className="text-white/50 text-sm">minutes</span>
              </div>
            )}
          </div>

          {/* Venice Water Taxi option (4th Venice sub-picker option) */}
          <div className="border-t border-white/5 pt-5" data-testid="water-taxi-section">
            <div className="flex items-center justify-between mb-3">
              <div>
                <label className="text-white/60 text-xs uppercase block">Venice "Water Taxi" option</label>
                <p className="text-white/40 text-[10px] mt-1">
                  Adds a 4th Venice sub-option (alongside Piazzale Roma, Airport, Near Venice) for hotels reachable only by boat in the historic centre.
                  When selected, the configured fixed surcharge is added per leg (×2 on round trips) and the night-service multiplier also applies to it.
                </p>
              </div>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form.water_taxi_enabled}
                  onChange={(e) => setForm(p => ({ ...p, water_taxi_enabled: e.target.checked }))}
                  className="accent-[#C5A059] w-4 h-4"
                  data-testid="water-taxi-toggle"
                />
                <span className="text-white/80 text-sm select-none">
                  {form.water_taxi_enabled ? "Enabled" : "Disabled"}
                </span>
              </label>
            </div>
            {form.water_taxi_enabled && (
              <div className="flex items-center gap-3 mt-2">
                <label className="text-white/60 text-xs uppercase">Surcharge per leg</label>
                <span className="text-white/50">€</span>
                <Input
                  type="number" min="0" step="1"
                  value={form.water_taxi_price}
                  onChange={(e) => setForm(p => ({ ...p, water_taxi_price: Math.max(0, parseFloat(e.target.value) || 0) }))}
                  className="input-dark w-28"
                  data-testid="water-taxi-price"
                />
              </div>
            )}
          </div>

          {/* Venice "Hotel in Venice" option — global kill switch for the 5th sub-picker button */}
          <div className="border-t border-white/5 pt-5" data-testid="hotel-in-venice-section">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-white/60 text-xs uppercase block">Venice &quot;Hotel in Venice&quot; option</label>
                <p className="text-white/40 text-[11px] mt-1 max-w-xl leading-relaxed">
                  Enables the 5th Venice sub-picker pill (&quot;Hotel in Venice&quot;) + the dedicated pages at
                  <code className="text-[#C5A059] mx-1">/hotels-in-venice</code> and per-hotel landing pages.
                  When disabled, the pill disappears from the booking form, the hotels endpoint returns an empty list,
                  and any incoming quote touching the hotel option is rejected. Individual hotels remain editable in Admin → Venice Hotels.
                </p>
              </div>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form.hotel_in_venice_enabled}
                  onChange={(e) => setForm(p => ({ ...p, hotel_in_venice_enabled: e.target.checked }))}
                  className="w-4 h-4 accent-[#C5A059]"
                  data-testid="hotel-in-venice-toggle"
                />
                <span className={`text-sm ${form.hotel_in_venice_enabled ? "text-[#C5A059]" : "text-white/50"}`}>
                  {form.hotel_in_venice_enabled ? "Enabled" : "Disabled"}
                </span>
              </label>
            </div>
          </div>

          {/* Passenger-count surcharge brackets */}
          <div className="border-t border-white/5 pt-5" data-testid="passenger-brackets-section">
            <div className="flex items-center justify-between mb-3">
              <div>
                <label className="text-white/60 text-xs uppercase block">Passenger-count surcharges</label>
                <p className="text-white/40 text-[10px] mt-1">
                  Add a surcharge that kicks in when the group falls inside a passenger range. First matching bracket wins.
                </p>
              </div>
              <Button
                type="button"
                onClick={() => setForm(p => ({
                  ...p,
                  passenger_brackets: [...(p.passenger_brackets || []), { min: 1, max: 4, type: "percent", value: 0, label: "" }]
                }))}
                size="sm"
                className="btn-gold text-xs"
                data-testid="add-pax-bracket"
              >
                + Bracket
              </Button>
            </div>
            {(form.passenger_brackets || []).length === 0 ? (
              <p className="text-white/30 text-xs italic">No brackets defined.</p>
            ) : (
              <div className="space-y-2">
                {form.passenger_brackets.map((b, idx) => (
                  <div key={idx} className="grid grid-cols-[1fr_1fr_1fr_1fr_2fr_auto] gap-2 items-end bg-[#0F0F0F] border border-white/5 rounded-sm p-3" data-testid={`pax-bracket-${idx}`}>
                    <div>
                      <label className="text-white/40 text-[10px] uppercase block mb-1">Min pax</label>
                      <Input type="number" min="1" max="99" value={b.min}
                        onChange={(e) => setForm(p => {
                          const next = [...p.passenger_brackets];
                          next[idx] = { ...next[idx], min: parseInt(e.target.value, 10) || 1 };
                          return { ...p, passenger_brackets: next };
                        })}
                        className="input-dark font-mono"
                        data-testid={`pax-bracket-${idx}-min`} />
                    </div>
                    <div>
                      <label className="text-white/40 text-[10px] uppercase block mb-1">Max pax</label>
                      <Input type="number" min="1" max="99" value={b.max}
                        onChange={(e) => setForm(p => {
                          const next = [...p.passenger_brackets];
                          next[idx] = { ...next[idx], max: parseInt(e.target.value, 10) || 1 };
                          return { ...p, passenger_brackets: next };
                        })}
                        className="input-dark font-mono"
                        data-testid={`pax-bracket-${idx}-max`} />
                    </div>
                    <div>
                      <label className="text-white/40 text-[10px] uppercase block mb-1">Type</label>
                      <select
                        value={b.type}
                        onChange={(e) => setForm(p => {
                          const next = [...p.passenger_brackets];
                          next[idx] = { ...next[idx], type: e.target.value };
                          return { ...p, passenger_brackets: next };
                        })}
                        className="input-dark w-full bg-[#1F1F1F] text-white text-sm rounded-sm h-10 px-2"
                        data-testid={`pax-bracket-${idx}-type`}
                      >
                        <option value="percent">Percent (%)</option>
                        <option value="fixed">Fixed (€)</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-white/40 text-[10px] uppercase block mb-1">Value</label>
                      <Input type="number" min="0" step="0.5" value={b.value}
                        onChange={(e) => setForm(p => {
                          const next = [...p.passenger_brackets];
                          next[idx] = { ...next[idx], value: parseFloat(e.target.value) || 0 };
                          return { ...p, passenger_brackets: next };
                        })}
                        className="input-dark font-mono"
                        data-testid={`pax-bracket-${idx}-value`} />
                    </div>
                    <div>
                      <label className="text-white/40 text-[10px] uppercase block mb-1">Label (optional)</label>
                      <Input value={b.label || ""}
                        onChange={(e) => setForm(p => {
                          const next = [...p.passenger_brackets];
                          next[idx] = { ...next[idx], label: e.target.value };
                          return { ...p, passenger_brackets: next };
                        })}
                        className="input-dark"
                        placeholder="e.g. 6-7 pax surcharge"
                        data-testid={`pax-bracket-${idx}-label`} />
                    </div>
                    <button
                      type="button"
                      onClick={() => setForm(p => ({ ...p, passenger_brackets: p.passenger_brackets.filter((_, i) => i !== idx) }))}
                      className="text-red-400/70 hover:text-red-400 px-2 py-2"
                      data-testid={`pax-bracket-${idx}-remove`}
                      title="Remove"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Button onClick={save} disabled={saving} className="btn-gold w-full" data-testid="save-settings-btn">
            <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Booking Settings"}
          </Button>
        </div>
      </section>
    </div>
  );
}

